import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";

import { getAppApiUrlBase } from "../Global";
import ResponseModel from "../models/ResponseModel";

import { getLoggerDiagnosticsSnapshot, logUnknownError, UNKNOWN_ERROR_MESSAGE } from "./logger/LoggerService";
import LoginService from "./login/LoginService";

// Logout handler — set once at app startup
let logoutHandler: (() => void) | null = null;

type ApiRequestMetadata = {
  requestId: string;
  startedAt: number;
  startedAtIso: string;
};

type ApiDebugInfo = {
  requestId: string;
  method: string;
  url: string;
  absoluteUrl: string;
  baseURL: string;
  startedAt: string;
  durationMs: number;
  axiosCode?: string;
  responseStatus?: number;
  xhrReadyState?: number;
  xhrStatus?: number;
  responseURL?: string;
  responseHeaders?: unknown;
  isNetworkError: boolean;
  probableCause: string;
  pageOrigin: string;
  requestOrigin: string;
  isCrossOrigin: boolean;
  hasAuthorizationHeader: boolean;
  contentType?: string;
  customHeaders: string[];
  willTriggerPreflight: boolean;
  isMixedContentRisk: boolean;
  hasServiceWorkerController: boolean;
  browser: Record<string, unknown>;
  pendingRequests: ApiPendingRequestSnapshot[];
};

type ApiPendingRequestSnapshot = {
  requestId: string;
  method: string;
  url: string;
  startedAt: string;
  durationMs: number;
};

type ApiRequestConfig = AxiosRequestConfig & {
  _retryCount?: number;
  metadata?: ApiRequestMetadata;
};

type ApiInternalRequestConfig = InternalAxiosRequestConfig & {
  metadata?: ApiRequestMetadata;
};

const pendingRequests = new Map<string, ApiPendingRequestSnapshot>();
const SEND_CLIENT_REQUEST_DEBUG_HEADERS = process.env.REACT_APP_SEND_CLIENT_REQUEST_DEBUG_HEADERS === "true";

const createRequestId = (): string => {
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
};

const getDurationMs = (startedAt?: number): number => {
  return startedAt ? Date.now() - startedAt : 0;
};

const buildPendingRequestsSnapshot = (excludeRequestId?: string): ApiPendingRequestSnapshot[] => {
  return Array.from(pendingRequests.values())
    .filter((request: ApiPendingRequestSnapshot): boolean => request.requestId !== excludeRequestId)
    .map(
      (request: ApiPendingRequestSnapshot): ApiPendingRequestSnapshot => ({
        ...request,
        durationMs: getDurationMs(Date.parse(request.startedAt)),
      }),
    )
    .sort((a: ApiPendingRequestSnapshot, b: ApiPendingRequestSnapshot): number => b.durationMs - a.durationMs);
};

type RequestNetworkShape = {
  absoluteUrl: string;
  pageOrigin: string;
  requestOrigin: string;
  isCrossOrigin: boolean;
  hasAuthorizationHeader: boolean;
  contentType?: string;
  customHeaders: string[];
  willTriggerPreflight: boolean;
  isMixedContentRisk: boolean;
  hasServiceWorkerController: boolean;
};

const getRequestHeaders = (config?: ApiRequestConfig): Record<string, string> => {
  const headers = config?.headers as Record<string, unknown> | undefined;
  if (!headers) {
    return {};
  }

  return Object.entries(headers).reduce((accumulator: Record<string, string>, [key, value]: [string, unknown]) => {
    if (typeof value === "string") {
      accumulator[key] = value;
    }
    return accumulator;
  }, {});
};

const buildRequestNetworkShape = (config?: ApiRequestConfig): RequestNetworkShape => {
  const headers = getRequestHeaders(config);
  const pageOrigin = window.location.origin;
  const absoluteUrl = new URL(config?.url ?? "", config?.baseURL ?? getAppApiUrlBase()).toString();
  const requestOrigin = new URL(absoluteUrl).origin;
  const headerNames = Object.keys(headers);
  const customHeaders = headerNames.filter((headerName: string): boolean => {
    const normalizedHeaderName = headerName.toLowerCase();
    return !["accept", "accept-language", "content-language", "content-type"].includes(normalizedHeaderName);
  });
  const contentType = headers["Content-Type"] ?? headers["content-type"];
  const hasAuthorizationHeader = headerNames.some(
    (headerName: string): boolean => headerName.toLowerCase() === "authorization",
  );
  const isCrossOrigin = requestOrigin !== pageOrigin;
  const isSimpleContentType =
    contentType === undefined ||
    ["application/x-www-form-urlencoded", "multipart/form-data", "text/plain"].includes(contentType.toLowerCase());
  const willTriggerPreflight =
    isCrossOrigin &&
    ((config?.method ?? "get").toUpperCase() !== "GET" ||
      hasAuthorizationHeader ||
      customHeaders.length > 0 ||
      !isSimpleContentType);

  return {
    absoluteUrl,
    pageOrigin,
    requestOrigin,
    isCrossOrigin,
    hasAuthorizationHeader,
    contentType,
    customHeaders,
    willTriggerPreflight,
    isMixedContentRisk: window.location.protocol === "https:" && absoluteUrl.startsWith("http://"),
    hasServiceWorkerController: !!navigator.serviceWorker?.controller,
  };
};

const resolveProbableCause = (
  config: ApiRequestConfig | undefined,
  error: AxiosError<ResponseModel>,
  durationMs: number,
): string => {
  const xhr = error.request as XMLHttpRequest | undefined;
  const networkShape = buildRequestNetworkShape(config);

  if (error.code === "ERR_CANCELED") {
    return "request_cancelled_in_browser";
  }

  if (!navigator.onLine) {
    return "browser_went_offline";
  }

  if (xhr?.status === 0 && durationMs >= 60000) {
    return "connection_dropped_after_long_running_request_possible_proxy_timeout";
  }

  if (networkShape.isMixedContentRisk) {
    return "browser_blocked_mixed_content_request_before_network";
  }

  if (networkShape.willTriggerPreflight && xhr?.status === 0 && xhr?.responseURL === "" && durationMs < 5000) {
    return "possible_cors_preflight_failure_or_browser_policy_block_before_actual_request";
  }

  if (networkShape.willTriggerPreflight && xhr?.status === 0 && xhr?.responseURL === "") {
    return "cross_origin_request_failed_without_http_response_possible_preflight_timeout_or_connection_close";
  }

  if (xhr?.status === 0 && xhr?.readyState === 4) {
    return "request_sent_but_connection_closed_without_http_response";
  }

  if (xhr?.status === 0) {
    return "network_transport_interrupted_before_http_response";
  }

  return "unknown_transport_failure";
};

const buildDebugInfo = (config?: ApiRequestConfig, error?: AxiosError<ResponseModel>): ApiDebugInfo => {
  const metadata: ApiRequestMetadata | undefined = config?.metadata;
  const xhr = error?.request as XMLHttpRequest | undefined;
  const durationMs = getDurationMs(metadata?.startedAt);
  const networkShape = buildRequestNetworkShape(config);

  return {
    requestId: metadata?.requestId ?? "unknown",
    method: (config?.method ?? "get").toUpperCase(),
    url: config?.url ?? "",
    absoluteUrl: networkShape.absoluteUrl,
    baseURL: config?.baseURL ?? getAppApiUrlBase(),
    startedAt: metadata?.startedAtIso ?? new Date().toISOString(),
    durationMs,
    axiosCode: error?.code,
    responseStatus: error?.response?.status,
    xhrReadyState: xhr?.readyState,
    xhrStatus: xhr?.status,
    responseURL: xhr?.responseURL,
    responseHeaders: error?.response?.headers,
    isNetworkError: error?.message === "Network Error" || error?.code === "ERR_NETWORK",
    probableCause: error ? resolveProbableCause(config, error, durationMs) : "unknown_transport_failure",
    pageOrigin: networkShape.pageOrigin,
    requestOrigin: networkShape.requestOrigin,
    isCrossOrigin: networkShape.isCrossOrigin,
    hasAuthorizationHeader: networkShape.hasAuthorizationHeader,
    contentType: networkShape.contentType,
    customHeaders: networkShape.customHeaders,
    willTriggerPreflight: networkShape.willTriggerPreflight,
    isMixedContentRisk: networkShape.isMixedContentRisk,
    hasServiceWorkerController: networkShape.hasServiceWorkerController,
    browser: getLoggerDiagnosticsSnapshot(),
    pendingRequests: buildPendingRequestsSnapshot(metadata?.requestId),
  };
};

const registerPendingRequest = (config: ApiInternalRequestConfig): void => {
  if (!config.metadata) {
    return;
  }

  pendingRequests.set(config.metadata.requestId, {
    requestId: config.metadata.requestId,
    method: (config.method ?? "get").toUpperCase(),
    url: config.url ?? "",
    startedAt: config.metadata.startedAtIso,
    durationMs: 0,
  });
};

const unregisterPendingRequest = (config?: ApiRequestConfig): void => {
  const requestId = config?.metadata?.requestId;
  if (requestId) {
    pendingRequests.delete(requestId);
  }
};

export const setApiLogoutHandler = (handler: () => void): void => {
  logoutHandler = handler;
};

// Shared Axios instance
const apiInstance: AxiosInstance = axios.create({
  baseURL: getAppApiUrlBase(),
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

// Request interceptor: attach auth token
apiInstance.interceptors.request.use((config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const requestConfig = config as ApiInternalRequestConfig;
  const token: string | null = localStorage.getItem("micra_token");
  const metadata: ApiRequestMetadata = {
    requestId: createRequestId(),
    startedAt: Date.now(),
    startedAtIso: new Date().toISOString(),
  };

  requestConfig.metadata = metadata;

  if (SEND_CLIENT_REQUEST_DEBUG_HEADERS && requestConfig.headers) {
    requestConfig.headers["X-Client-Request-Id"] = metadata.requestId;
    requestConfig.headers["X-Client-Request-Started-At"] = metadata.startedAtIso;
  }

  if (token && requestConfig.headers) {
    requestConfig.headers["Authorization"] = token;
  }

  registerPendingRequest(requestConfig);
  return requestConfig;
});

// Response interceptor: handle 401 → refresh → retry
apiInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    unregisterPendingRequest(response.config as ApiRequestConfig);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as ApiRequestConfig;

    if (error.response?.status === 401) {
      const retryCount = originalRequest._retryCount ?? 0;

      if (retryCount < 3) {
        originalRequest._retryCount = retryCount + 1;
        const refreshResult: ResponseModel = await LoginService.onRefresh();
        const refreshSucceeded = refreshResult.status >= 200 && refreshResult.status < 300 && !refreshResult.error;

        if (!refreshSucceeded) {
          unregisterPendingRequest(originalRequest);
          if (refreshResult.status === 401 && logoutHandler) logoutHandler();
          return Promise.reject(error);
        }

        // Token refreshed — retry original request
        const token = localStorage.getItem("micra_token");
        if (token && originalRequest.headers) {
          originalRequest.headers["Authorization"] = token;
        }
        unregisterPendingRequest(originalRequest);
        return apiInstance(originalRequest);
      }

      // Exhausted retries
      unregisterPendingRequest(originalRequest);
      if (logoutHandler) logoutHandler();
      return Promise.reject(error);
    }

    unregisterPendingRequest(originalRequest);
    return Promise.reject(error);
  },
);

// Default error response
const defaultError = (): ResponseModel => {
  return {
    status: 400,
    data: null,
    error: {
      message: UNKNOWN_ERROR_MESSAGE,
    },
  };
};

const debugErrorResponse = (config: ApiRequestConfig | undefined, error: AxiosError<ResponseModel>): ResponseModel => {
  return {
    ...defaultError(),
    debug: buildDebugInfo(config, error),
  };
};

// Shared request helpers
export const apiPost = async (url: string, data: Record<string, unknown> = {}): Promise<ResponseModel> => {
  try {
    const response: { data: ResponseModel } = await apiInstance.post(url, data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr: AxiosError<ResponseModel> = error as AxiosError<ResponseModel>;
    if (axiosErr.response?.data) {
      return axiosErr.response.data;
    }

    const requestConfig = axiosErr.config as ApiRequestConfig | undefined;
    const debugInfo = buildDebugInfo(requestConfig, axiosErr);

    logUnknownError("apiPost", error, {
      url,
      data,
      debug: debugInfo,
    });

    return debugErrorResponse(requestConfig, axiosErr);
  }
};

export const apiPut = async (url: string, data: Record<string, unknown> = {}): Promise<ResponseModel> => {
  try {
    const response: { data: ResponseModel } = await apiInstance.put(url, data);
    return response.data;
  } catch (error: unknown) {
    const axiosErr: AxiosError<ResponseModel> = error as AxiosError<ResponseModel>;
    if (axiosErr.response?.data) {
      return axiosErr.response.data;
    }

    const requestConfig = axiosErr.config as ApiRequestConfig | undefined;
    const debugInfo = buildDebugInfo(requestConfig, axiosErr);

    logUnknownError("apiPut", error, {
      url,
      data,
      debug: debugInfo,
    });

    return debugErrorResponse(requestConfig, axiosErr);
  }
};

export const apiGet = async (url: string): Promise<ResponseModel> => {
  try {
    const response: { data: ResponseModel } = await apiInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    const axiosErr: AxiosError<ResponseModel> = error as AxiosError<ResponseModel>;
    if (axiosErr.response?.data) {
      return axiosErr.response.data;
    }

    const requestConfig = axiosErr.config as ApiRequestConfig | undefined;
    const debugInfo = buildDebugInfo(requestConfig, axiosErr);

    logUnknownError("apiGet", error, {
      url,
      debug: debugInfo,
    });

    return debugErrorResponse(requestConfig, axiosErr);
  }
};

export const apiDelete = async (url: string): Promise<ResponseModel> => {
  try {
    const response: { data: ResponseModel } = await apiInstance.delete(url);
    return response.data;
  } catch (error: unknown) {
    const axiosErr: AxiosError<ResponseModel> = error as AxiosError<ResponseModel>;
    if (axiosErr.response?.data) {
      return axiosErr.response.data;
    }

    const requestConfig = axiosErr.config as ApiRequestConfig | undefined;
    const debugInfo = buildDebugInfo(requestConfig, axiosErr);

    logUnknownError("apiDelete", error, {
      url,
      debug: debugInfo,
    });

    return debugErrorResponse(requestConfig, axiosErr);
  }
};
